import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {EiImportService} from "../../../service/api/ei-import.service";
import {EiImportStatusInternal} from "../../../enum/ei-import-status-internal.enum";
import {ImSchemaFileService} from "../../../../im/service/api/im-schema-file.service";
import {EmSchemaFieldDto} from "../../../../em/model/dto/entity/em-schema-field.dto";
//import {OverlayPanel} from "primeng/overlaypanel";
import {WidgetHelperButton} from "../../../../../core/shared/basic/widget-helper-button";
import {WidgetData} from "../../../../../core/shared/widget/widget-data";
import {MvsFormValueListEntryDto} from "../../../../../core/shared/dto/form/dto/mvs-form-value-list-entry.dto";
import {MvsObjectNavigationService} from "../../../../../core/shared/navigation/mvs-object-navigation.service";
import {DtoTemplate} from "../../../../../core/shared/dto/dto-template";
import {ObjectRequestListGroupBy} from "../../../../../core/shared/dto/object/request/object-request-list-group-by";
import {ObjectRequestListAttribute} from "../../../../../core/shared/dto/object/request/object-request-list-attribute";
import {
    DtoListAttributeRequestAggregateEnum
} from "../../../../../core/shared/dto/enum/dto-list-attribute-request-aggregate.enum";
import {
    ObjectRequestEntityProcessingTypeEnum
} from "../../../../../core/shared/dto/object/request/object-request-entity-processing-type.enum";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {Sorting} from "../../../../../core/shared/misc/sorting";
import {WidgetFactory} from "../../../../../core/shared/widget/service/widget.factory";
import {ObjectIdentifierData} from "../../../../../core/shared/basic/object-identifier-data";
import {MvsObjectNavigationEntry} from "../../../../../core/shared/navigation/mvs-object-navigation-entry";
import {MvsObjectNavigationActionEnum} from "../../../../../core/shared/navigation/mvs-object-navigation-action-enum";
import {Popover} from "primeng/popover";
import {QlJoinDto} from "../../../../../core/shared/ql/data/dto/ql-join.dto";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";


@Component({
    selector: 'ei-import-table-widget-component',
    templateUrl: 'ei-import-table-widget.component.html',
    standalone: false
})
export class EiImportTableWidgetComponent implements OnInit, OnChanges {

    busy: boolean;  // indicator whether the component is busy
    initialized: boolean; // indicator whether the component was initialized

    /**
     * Additional filter criteria supplied by a parent component (e.g.
     * Phase / Source-System / Schema dropdowns on the new Importe tab).
     * Composed with the widget's internal status filter when building the
     * underlying QL request. When this array changes the widget is
     * recomputed.
     */
    @Input() additionalFilterCriteria: FilterCriteria[] = [];

    widgetEiImports: WidgetData;
    statusEntries: MvsFormValueListEntryDto[];
    schemaFileList: EmSchemaFieldDto[];
    selectedSchema: EmSchemaFieldDto;

    /**
     * Multi-select status filter.
     *   0 entries → no status filter is applied
     *   1 entry   → single FilterCriteria.create('e.status', =, ordinal)
     *   N entries → FilterCriteria.createOr(... N equals-criteria)
     * Seeded from the `?importStatus=X` query param for deep-link compatibility
     * with the Overview KPI tiles.
     */
    selectedStatuses: number[] = [];

    selectedImportId: number;
    totalCount: number;


    widgetToolbarButtons: WidgetHelperButton[] = [
        {
            label: 'Upload Schema',
            icon: `fa-solid fa-file-arrow-up`,
            display: true,
            action: 'uploadSchema',
            tooltip: 'Upload Schema',
            style: {
                'padding': '1px 8px 1px 4px',
                'border-radius': '4px',
                'background-color': 'var(--primary-color) ',
                'transition': 'all 0.3s ',
            },

            iconStyle: {
                'color': 'var(--surface-0)',
                'font-size': '1.1rem',
            },
            labelStyle: {
                'font-weight': '500',
                'font-size': '0.95rem',
                'color': 'var(--surface-0)',
            }
        },
    ];

    constructor(
        protected navigationService: MvsObjectNavigationService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected eiImportService: EiImportService,
        protected schemaFileService: ImSchemaFileService) {

    }

    ngOnInit(): void {
        this.initComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Recompute the widget whenever the parent-driven filter criteria
        // change. Only fires after the component has been initialised
        // (ngOnInit runs `initComponent` which eventually triggers the first
        // `refreshComponent` via the template subscription).
        if (this.initialized && changes['additionalFilterCriteria']) {
            this.refreshComponent();
        }
    }

    /**
     * Initialize Component.
     */
    initComponent() {

        this.eiImportService.template(new DtoTemplate()).subscribe(res => {
            this.statusEntries = res.getFormField('status').valueList.entries;
            this.getStatusCount();
        });

    }

    getStatusCount() {

        this.totalCount = 0;

        const objectRequestList = ObjectRequestListGroupBy.create(
            false,
            [],
            [],
            ['status'],
            [new ObjectRequestListAttribute('status', "Anzahl", DtoListAttributeRequestAggregateEnum.count)],
            ObjectRequestEntityProcessingTypeEnum.regular
        );

        this.eiImportService.groupBy(objectRequestList).subscribe(groupBy => {
            for (let statusEntry of this.statusEntries) {
                statusEntry['uiSelected'] = false;
                const findStatus = groupBy.entries.find(entry => entry['status'] == statusEntry.key);

                if (findStatus) {
                    statusEntry['count'] = findStatus['status_count'];
                    this.totalCount += findStatus['status_count'];
                } else {
                    statusEntry['count'] = 0;
                }
            }
            this.refreshComponent();
        });
    }

    /**
     * Refresh Component.
     *
     * Seeds `selectedStatuses` from the `?importStatus=X` query param so deep-
     * links from the Overview KPI tiles still land on a filtered table. The
     * param is read once on entry; subsequent toggles mutate `selectedStatuses`
     * directly and rebuild the widget without a router roundtrip.
     */
    refreshComponent() {

        this.route.queryParamMap.subscribe(params => {
            const importStatus = params.get('importStatus');

            if (importStatus) {
                const statusKey = Number(importStatus);
                this.selectedStatuses = Number.isFinite(statusKey) ? [statusKey] : [];
            } else {
                this.selectedStatuses = [];
            }

            this.initImportWidget();

            this.initialized = true;

        });

    }

    /**
     * Toggle a status into / out of the multi-select filter.
     *   statusKey === null  → clicking "Alle": clear all selections
     *   already selected    → remove from array
     *   not selected        → add to array
     * Rebuilds the widget on every change. No router navigation is performed —
     * selections are in-memory state on this component.
     */
    toggleStatus(statusKey: number | null): void {
        if (statusKey === null) {
            if (this.selectedStatuses.length === 0) {
                return; // already showing all
            }
            this.selectedStatuses = [];
        } else {
            const idx = this.selectedStatuses.indexOf(statusKey);
            if (idx >= 0) {
                // Toggle off — new array so OnPush consumers notice.
                this.selectedStatuses = this.selectedStatuses.filter(k => k !== statusKey);
            } else {
                this.selectedStatuses = [...this.selectedStatuses, statusKey];
            }
        }
        this.initImportWidget();
    }

    initImportWidget() {

        let filterCriteria: FilterCriteria[] = [];

        // Multi-select status filter via FilterCriteria OR composition.
        //   EiImport.status is @Enumerated(ORDINAL) → plain QL field 'e.status'
        //   (not a FK, so no Id-suffix).
        //   0 selected → no status filter pushed
        //   1 selected → single equals-criterion
        //   N selected → FilterCriteria.createOr(...N equals-criteria)
        if (this.selectedStatuses.length === 1) {
            filterCriteria.push(FilterCriteria.create(
                'e.status',
                FilterCriteria.cOperatorEqual,
                this.selectedStatuses[0]
            ));
        } else if (this.selectedStatuses.length > 1) {
            filterCriteria.push(FilterCriteria.createOr(
                ...this.selectedStatuses.map(k =>
                    FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual, k)
                )
            ));
        }

        // Compose parent-supplied filters (Phase / Source-System / Schema
        // on the new Importe tab) with the internal status filter. Null-safe:
        // when no parent is mounted `additionalFilterCriteria` defaults to [].
        if (this.additionalFilterCriteria && this.additionalFilterCriteria.length > 0) {
            filterCriteria = filterCriteria.concat(this.additionalFilterCriteria);
        }

        const qlJoins: QlJoinDto[] = [
            {
                joinType: "left",
                type: "entity",
                name: "importType",
                as: "it",
            },
            {
                // AI: Join to ObjectType via EiImport.sourceObjectType (ManyToOne FK).
                // Projects sot.name as the "Quelle" column value and sot.id for reference.
                // This join is left-outer so rows without a sourceObjectType still appear.
                joinType: "left",
                type: "entity",
                name: "sourceObjectType",
                as: "sot",
                fields: [
                    { name: "id" },
                    { name: "name", label: "Quelle" },
                ],
            },
        ];

            this.widgetEiImports = WidgetFactory.createWidgetQl(
            "ei.import.dashboard.imports.table.new",
            "Importe",
            "ei.EiImport",
                "table",
                filterCriteria,
                [new Sorting("e.createdDate", false)],
                qlJoins
        );
    }

    navigateToObject(id: number) {
        // OLD: top-level absolute route navigation (kept as comment per the
        // refactor brief — "don't remove any code").
        // this.router.navigate(['/ei/import', id, 'detail']);

        // PREVIOUS ATTEMPT (kept as comment): opened the OBC in the right
        // sidebar pane — but the requirement is to open the EiImport OBC as
        // a full new page, not as a side panel. Replaced by the `'main'`
        // location call below.
        // const entryRight = MvsObjectNavigationEntry.createNavigationEntry(
        //     'ei.EiImport', id, null, null, null, null,
        //     MvsObjectNavigationActionEnum.any, 'side',
        // );
        // this.navigationService.navigateTo(entryRight, 'right');

        // NEW: open the EiImport OBC as a full page in the main view.
        // Same pattern as `ei-config.page.ts` (handleEiImportTypeSelect):
        // target `'object'` on the entry + `'main'` on the navigateTo call
        // routes through the navigation service to the registered
        // `EiImportObjectComponent` (see `ei.entity-provider.ts`) and
        // replaces the current page rather than overlaying a sidebar.
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'ei.EiImport',
            id,
            'object',
            '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit,
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    handleObjectSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        this.selectedImportId = event.objectId;
        // in-place navigation replaced by openImportInNewTab on dblclick (LG pattern)
        // this.navigateToObject(event.objectId);
    }

    openImportInNewTab(_event: MouseEvent) {
        if (!this.selectedImportId) return;
        this.navigationService.handleObjectNavigation(
            new ObjectIdentifier('ei.EiImport', this.selectedImportId),
            null,
            {openNewTab: true}
        );
    }

    handleToolbarButtonClick(overlay: Popover, event: WidgetHelperButton) {


        this.schemaFileService.list(ObjectRequestList.createBasic(false, [], [])).subscribe(response => {
            this.schemaFileList = response.entries;
            overlay.toggle(event.mouseEvent);
        });
    }

    handleSchemaNavigation() {

        const entry = MvsObjectNavigationEntry.createNavigationEntry('im.ImSchemaFile', this.selectedSchema.id, null, null, null, null, MvsObjectNavigationActionEnum.any, 'side');
        this.navigationService.navigateTo(entry, 'right');
    }


    protected readonly EiImportStatusInternal = EiImportStatusInternal;
}
